(() => {
  const blockSelector = '[data-free-gift-block]';

  function getRoot() {
    return (window.Shopify && window.Shopify.routes)
      ? window.Shopify.routes.root
      : '/';
  }

  function cartUrl(path) {
    return `${getRoot()}${path}`;
  }

  async function getCart() {
    const response = await fetch(cartUrl('cart.js'), {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Unable to load cart');
    }

    return response.json();
  }

  async function addCartItem(item) {
    const response = await fetch(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [item] })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Free gift] cart/add.js failed:', errorText);
      throw new Error('Unable to add item');
    }

    return response.json();
  }

  async function changeCartLine(lineKey, quantity) {
    const response = await fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: lineKey, quantity })
    });

    if (!response.ok) {
      throw new Error('Unable to update cart');
    }

    return response.json();
  }

  function refreshThemeCart() {
    [
      'free-gift:updated',
      'cart:refresh',
      'cart:updated',
      'on:cart:change',
      'on:cart:refresh'
    ].forEach(eventName => {
      document.dispatchEvent(
        new CustomEvent(eventName, { bubbles: true })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent(eventName, { bubbles: true })
      );
    });
  }

  function initBlock(block) {
    if (block.dataset.freeGiftInitialised === 'true') return;
    block.dataset.freeGiftInitialised = 'true';

    const giftProductId = String(block.dataset.giftProductId);
    const promotionId = block.dataset.promotionId;

    const variantInput = block.querySelector('[data-free-gift-variant]');
    const button = block.querySelector('[data-free-gift-button]');
    const buttonText = block.querySelector('[data-free-gift-button-text]');
    const buttonLoading = block.querySelector('[data-free-gift-button-loading]');
    const status = block.querySelector('[data-free-gift-status]');

    function setStatus(message) {
      if (status) status.textContent = message || '';
    }

    function isGift(item) {
      return (
        String(item.product_id) === giftProductId &&
        item.properties &&
        item.properties._free_gift === 'true' &&
        item.properties._free_gift_promotion === promotionId
      );
    }

    function cartHasQualifyingProduct(cart) {
      return cart.items.some(
        item => String(item.product_id) !== giftProductId
      );
    }

    async function removeExistingGifts(cart) {
      const gifts = cart.items.filter(isGift);

      for (const gift of gifts) {
        await changeCartLine(gift.key, 0);
      }
    }

    function setLoading(loading) {
      if (button) button.disabled = loading;
      if (buttonText) buttonText.hidden = loading;
      if (buttonLoading) buttonLoading.hidden = !loading;
    }

    function updateGiftImage() {
      if (!variantInput || !variantInput.options) return;

      const selectedOption =
        variantInput.options[variantInput.selectedIndex];

      const imageUrl = selectedOption && selectedOption.dataset.variantImage;

      const image = block.querySelector('[data-free-gift-image]');

      if (image && imageUrl) {
        image.src = imageUrl;
      }
    }

    function updateButtonState() {
      if (button && variantInput) {
        button.disabled = !variantInput.value;
      }
    }

    async function refresh() {
      try {
        const cart = await getCart();
        const qualifies = cartHasQualifyingProduct(cart);

        if (!qualifies) {
          await removeExistingGifts(cart);
          block.hidden = true;
          setStatus('');
          refreshThemeCart();
          return;
        }

        block.hidden = false;

        const gifts = cart.items.filter(isGift);

        if (gifts.length > 1) {
          for (const gift of gifts.slice(1)) {
            await changeCartLine(gift.key, 0);
          }
        }

        if (gifts.length >= 1) {
          setStatus('Your free gift is selected.');
        } else {
          setStatus('');
        }
      } catch (error) {
        console.error('[Free gift] Could not refresh block.', error);
      }
    }

    async function selectGift() {
      if (!variantInput || !variantInput.value) {
        setStatus('Please select an available gift variant.');
        return;
      }

      try {
        setLoading(true);
        setStatus('Adding your free gift...');

        const cart = await getCart();

        if (!cartHasQualifyingProduct(cart)) {
          setStatus(
            'Add a product to your cart first to claim your free gift.'
          );
          return;
        }

        await removeExistingGifts(cart);

        await addCartItem({
          id: Number(variantInput.value),
          quantity: 1,
          properties: {
            _free_gift: 'true',
            _free_gift_promotion: promotionId
          }
        });

        const afterCart = await getCart();
        const giftPersisted = afterCart.items.some(isGift);

        if (!giftPersisted) {
          console.error(
            '[Free gift] Gift was added but is gone on re-fetch. ' +
            'Another script or app is removing it.'
          );
          setStatus(
            'The gift could not be kept in the cart. Please contact us.'
          );
          refreshThemeCart();
          return;
        }

        setStatus('Your free gift has been added.');

        refreshThemeCart();

        await refresh();
      } catch (error) {
        console.error('[Free gift]', error);
        setStatus('There was a problem adding the gift. Please try again.');
      } finally {
        setLoading(false);
        updateButtonState();
      }
    }

    if (variantInput) {
      variantInput.addEventListener('change', () => {
        updateGiftImage();
        updateButtonState();
      });
    }

    if (button) {
      button.addEventListener('click', selectGift);
    }

    updateButtonState();
    refresh();
  }

  function initAllBlocks() {
    document.querySelectorAll(blockSelector).forEach(initBlock);
  }

  document.addEventListener('DOMContentLoaded', initAllBlocks);
  document.addEventListener('cart:updated', initAllBlocks);
  document.addEventListener('cart:refresh', initAllBlocks);
  document.addEventListener('free-gift:updated', initAllBlocks);
  document.addEventListener('shopify:section:load', initAllBlocks);

  if (document.readyState !== 'loading') {
    initAllBlocks();
  }
})();